import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminLogoHeader from "../../components/admin/AdminLogoHeader";
import api from "../../services/api";

const docFields = [
  { key: "passport_photo", label: "Passport photograph" },
  { key: "id_card", label: "Government ID" },
  { key: "signature_specimen", label: "Signature specimen" },
  { key: "utility_bill", label: "Utility bill" },
];

const initialForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  contact: "",
  phone_number: "",
  user_type: "individual",
  role: "user",
  date_of_birth: "",
  residential_address: "",
  country: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  bvn: "",
  admin_note: "",
  password: "",
  account_verified: true,
  kyc_approved: false,
};

const ComplianceManualCreate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState({
    passport_photo: null,
    id_card: null,
    signature_specimen: null,
    utility_bill: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const onFileChange = (event) => {
    const { name, files: selected } = event.target;
    setFiles((prev) => ({ ...prev, [name]: selected?.[0] || null }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setFiles({
      passport_photo: null,
      id_card: null,
      signature_specimen: null,
      utility_bill: null,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess(null);
    if (!form.contact.trim()) {
      setError("Email/username is required.");
      return;
    }
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("First name and last name are required.");
      return;
    }
    try {
      setSubmitting(true);
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          formData.append(key, file);
        }
      });
      const response = await api.adminCreateManualPendingUser(formData);
      setSuccess(response);
      resetForm();
    } catch (err) {
      setError(err.message || "Unable to create pending profile.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="mx-auto max-w-5xl p-6 space-y-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-emerald-600 hover:underline"
        >
          &larr; Back to compliance
        </button>

        <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <h1 className="text-xl font-semibold text-slate-900">Manual onboarding</h1>
            <p className="text-sm text-slate-500">
              Capture applicant KYC details on behalf of the compliance team. Attach supporting documents so the
              account can be reviewed in the approvals queue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {success ? (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <p className="font-semibold">
                  Pending profile #{success.pending_id} {success.resubmitted ? "resubmitted" : "created"} successfully.
                </p>
                {success.temporary_password ? (
                  <p className="mt-1">
                    Temporary password:{" "}
                    <span className="font-mono font-semibold">{success.temporary_password}</span>
                  </p>
                ) : null}
                <button
                  type="button"
                  className="mt-3 inline-flex rounded-lg border border-emerald-600 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                  onClick={() => navigate("/admin/compliance")}
                >
                  Go to approvals
                </button>
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">First name</label>
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Middle name</label>
                <input
                  type="text"
                  name="middle_name"
                  value={form.middle_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Last name</label>
                <input
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Contact (email or phone)</label>
                <input
                  type="text"
                  name="contact"
                  value={form.contact}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone number</label>
                <input
                  type="text"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={onChange}
                  maxLength={20}
                  inputMode="numeric"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Date of birth</label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={form.date_of_birth}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">User type</label>
                <select
                  name="user_type"
                  value={form.user_type}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="individual">Individual</option>
                  <option value="corporate">Corporate</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700">Residential address</label>
                <textarea
                  name="residential_address"
                  value={form.residential_address}
                  onChange={onChange}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Street, city, state"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Country</label>
                <input
                  type="text"
                  name="country"
                  value={form.country}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="e.g. Nigeria"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Role</label>
                <select
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  disabled
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
                >
                  <option value="user">User</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700">Internal note</label>
                <textarea
                  name="admin_note"
                  value={form.admin_note}
                  onChange={onChange}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Optional compliance note"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Bank name</label>
                <input
                  type="text"
                  name="bank_name"
                  value={form.bank_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Account name</label>
                <input
                  type="text"
                  name="bank_account_name"
                  value={form.bank_account_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Account number</label>
                <input
                  type="text"
                  name="bank_account_number"
                  value={form.bank_account_number}
                  onChange={onChange}
                  maxLength={20}
                  inputMode="numeric"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">BVN</label>
                <input
                  type="text"
                  name="bvn"
                  value={form.bvn}
                  onChange={onChange}
                  maxLength={11}
                  inputMode="numeric"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="account_verified"
                  type="checkbox"
                  name="account_verified"
                  checked={form.account_verified}
                  onChange={onChange}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="account_verified" className="text-sm text-slate-700">
                  Account verified
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="kyc_approved"
                  type="checkbox"
                  name="kyc_approved"
                  checked={form.kyc_approved}
                  onChange={onChange}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="kyc_approved" className="text-sm text-slate-700">
                  KYC approved
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700">Temporary password (optional)</label>
                <input
                  type="text"
                  name="password"
                  value={form.password}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Leave blank to auto-generate"
                />
              </div>
            </div>

            <div className="rounded-xl border border-dashed border-slate-200 px-4 py-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                Attach KYC documents
              </h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {docFields.map((field) => (
                  <label key={field.key} className="flex flex-col text-sm text-slate-600">
                    <span className="font-medium text-slate-700">{field.label}</span>
                    <input
                      type="file"
                      name={field.key}
                      accept=".png,.jpg,.jpeg,.pdf,.webp"
                      onChange={onFileChange}
                      className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                    {files[field.key] ? (
                      <span className="mt-1 text-xs text-emerald-600">
                        {files[field.key]?.name || "Selected"}
                      </span>
                    ) : (
                      <span className="mt-1 text-xs text-slate-400">No file chosen</span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-emerald-600 px-6 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {submitting ? "Saving..." : "Create pending profile"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ComplianceManualCreate;
